import { Fragment, useEffect, useState, useRef } from "react";
import { isNonHeaderCsvError } from "./common/csvErrors";

// ui
import {
  Typography,
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Alert,
  MenuItem,
  Autocomplete,
  TextField,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

// ★cmd-po_1142: PoModalShell + PoForm 乗せ替え
import { PoModalShell } from "./common/PoModalShell";
import { PoButton } from "./common/PoButton";
import { PoField, PoLabel, PoHelper, PoSelect, poAutocompleteSx } from "./common/PoForm";

// API
import { getSampleCSV } from "../api/presignedURL";

// util
import { parseCSVForPreview } from "../lib/csvPreview";

interface PostCSVModalProps {
  onModal: boolean;
  swCloseModal: () => void;
  postCSVStatus: {
    status: "none" | "open" | "posting" | "result" | "loading" | "error";
    message: string;
    result: { [key: string]: any }[];
    header_location: string;
    error_count?: number | null;
    total_count?: number | null;
    success_count?: number | null;
  };
  setPostCSVStatus: (status: {
    status: "none" | "open" | "posting" | "result" | "loading" | "error";
    message: string;
    result: { [key: string]: any }[];
    header_location: string;
    error_count?: number | null;
    total_count?: number | null;
    success_count?: number | null;
  }) => void;
  postCSVFunc: (postDatas: File) => void;
  postCSVTitle: string;
  postCSVColumn: { name: string; required: boolean }[];
  postCSVExtraChips?: string[];
  postCSVColumnResult: string[];
  postCSVPath: string;
  productList: {
    id: number;
    high_rank_company_id: number;
    product_name: string;
    high_rank_company_name: string;
  }[];
  categoryList: { id: number; category_name: string }[];
  sampleNote?: string;
  error_count?: number | null;
  total_count?: number | null;
  success_count?: number | null;
  resultBannerReflectsError?: boolean;
}

function foldCSVColumns(
  spec: { name: string; required: boolean }[]
): { label: string; required: boolean }[] {
  const chips: { label: string; required: boolean }[] = [];
  let i = 0;
  while (i < spec.length) {
    const cur = spec[i];
    const m = cur.name.match(/^(.*?)(\d+)$/);
    if (m) {
      const prefix = m[1];
      const startNum = parseInt(m[2], 10);
      let j = i + 1;
      while (j < spec.length) {
        const next = spec[j];
        const nm = next.name.match(/^(.*?)(\d+)$/);
        if (
          nm &&
          nm[1] === prefix &&
          parseInt(nm[2], 10) === startNum + (j - i) &&
          next.required === cur.required
        ) {
          j++;
        } else {
          break;
        }
      }
      if (j - i > 1) {
        const endNum = startNum + (j - i) - 1;
        chips.push({ label: `${prefix}${startNum}〜${endNum}`, required: cur.required });
        i = j;
      } else {
        chips.push({ label: cur.name, required: cur.required });
        i++;
      }
    } else {
      chips.push({ label: cur.name, required: cur.required });
      i++;
    }
  }
  return chips;
}

export function PostCSVModal(props: PostCSVModalProps) {
  const {
    onModal,
    swCloseModal,
    postCSVStatus,
    setPostCSVStatus,
    postCSVFunc,
    postCSVTitle,
    postCSVColumn,
    postCSVExtraChips,
    postCSVColumnResult,
    postCSVPath,
    productList,
    categoryList,
    sampleNote,
    error_count,
    total_count,
    success_count,
    resultBannerReflectsError,
  } = props;
  async function handleFileChange(
    event_file: React.ChangeEvent<HTMLInputElement>
  ) {
    if (event_file.target.files && event_file.target.files.length > 0) {
      const file = event_file.target.files[0];
      const { rowCount, headers, sample } = await parseCSVForPreview(file);
      const expectedCols = postCSVColumn.map((c) => c.name);
      const headerOk =
        expectedCols.length === 0 ||
        expectedCols.some((c) => headers.includes(c));
      rowCountSnapshotRef.current = 0;
      setPreviewFile(file);
      setPreviewRowCount(rowCount);
      setPreviewHeaders(headers);
      setPreviewSample(sample);
      setPreviewHeaderOk(headerOk);
    }
    event_file.target.files = null;
  }


  const [postCSVProductID, setPostCSVProductID] = useState<number | null>(null);
  const [postCSVProductName, setPostCSVProductName] = useState<string>("");
  const autocompleteProductList: string[] = productList.map((product) => {
    return product.high_rank_company_name === "-"
      ? product.product_name
      : product.high_rank_company_name + "/" + product.product_name;
  });
  useEffect(() => {
    let productID: null | number = null;
    productList.map((product) => {
      const _postCSVProductName =
        product.high_rank_company_name === "-"
          ? product.product_name
          : product.high_rank_company_name + "/" + product.product_name;
      if (postCSVProductName === _postCSVProductName) {
        productID = product.id;
      }
    });
    setPostCSVProductID(productID);
  }, [postCSVProductName]);

  const [postCSVCategoryID, setPostCSVCategoryID] = useState<number | null>(
    null
  );
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [previewRowCount, setPreviewRowCount] = useState<number>(0);
  const [previewHeaders, setPreviewHeaders] = useState<string[]>([]);
  const [previewSample, setPreviewSample] = useState<string[][]>([]);
  const [previewHeaderOk, setPreviewHeaderOk] = useState<boolean>(true);
  const rowCountSnapshotRef = useRef<number>(0);

  const dialogWidth =
    postCSVStatus.status === "open"
      ? "720px"
      : postCSVStatus.status === "result"
      ? "1200px"
      : "640px";

  // ★cmd-po_1142: const style 削除 → PoModalShell widthPx={dialogWidth} で担当

  useEffect(() => {
    if (!onModal) {
      setPreviewFile(null);
      setPreviewRowCount(0);
      setPreviewHeaders([]);
      setPreviewSample([]);
      setPreviewHeaderOk(true);
    }
  }, [onModal]);

  async function downloadSampleCSV() {
    setPostCSVStatus({
      ...postCSVStatus,
      status: "loading",
    });
    const product = productList.filter(
      (_product) => _product.id == postCSVProductID
    );
    const productName = product.length > 0 ? product[0].product_name : null;
    const category = categoryList.filter(
      (_category) => _category.id == postCSVCategoryID
    );
    const categoryName = category.length > 0 ? category[0].category_name : null;
    const header_location = await getSampleCSV(
      postCSVPath,
      postCSVProductID,
      productName,
      postCSVCategoryID,
      categoryName
    );
    if (header_location != "None") {
      const url = header_location;
      const link = document.createElement("a");
      link.href = header_location;
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);
      link.parentNode?.removeChild(link);
    }
    setPostCSVStatus({
      ...postCSVStatus,
      status: "open",
    });
  }

  async function downloadErrorList() {
    setPostCSVStatus({
      ...postCSVStatus,
      status: "loading",
    });
    const header_location = postCSVStatus.header_location;
    if (header_location != "None") {
      const url = header_location;
      const link = document.createElement("a");
      link.href = header_location;
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);
      link.parentNode?.removeChild(link);
    }
    setPostCSVStatus({
      ...postCSVStatus,
      status: "result",
    });
  }

  // ★cmd-po_1142: footer JSX (PoButton に変換)
  const footerNode = (postCSVStatus.status === "posting" || postCSVStatus.status === "loading") ? undefined : (
    <>
      {postCSVStatus.status === "open" && previewFile === null ? (
        <>
          <PoButton
            variant="secondary"
            data-testid="post-csv-cancel-button"
            onClick={() => {
              rowCountSnapshotRef.current = 0;
              swCloseModal();
              setPostCSVProductID(null);
            }}
          >
            キャンセル
          </PoButton>
          <input
            accept=".csv"
            style={{ display: "none" }}
            id="raised-button-file"
            type="file"
            onChange={(e) => handleFileChange(e)}
          />
          <label htmlFor="raised-button-file">
            <PoButton
              component="span"
              data-testid="post-csv-upload-button"
            >
              CSV取込
              <AttachFileIcon
                sx={{ marginTop: "-2.5px", marginLeft: "6px" }}
                fontSize="medium"
              />
            </PoButton>
          </label>
        </>
      ) : postCSVStatus.status === "open" && previewFile !== null ? (
        <>
          <PoButton
            variant="secondary"
            onClick={() => {
              rowCountSnapshotRef.current = 0;
              setPreviewFile(null);
              setPreviewRowCount(0);
              setPreviewHeaders([]);
              setPreviewSample([]);
              setPreviewHeaderOk(true);
            }}
            data-testid="post-csv-preview-back-button"
          >
            戻る
          </PoButton>
          <PoButton
            variant="secondary"
            disabled={previewRowCount === 0 || !previewHeaderOk}
            onClick={() => {
              const f = previewFile;
              rowCountSnapshotRef.current = previewRowCount;
              setPreviewFile(null);
              postCSVFunc(f!);
            }}
            data-testid="post-csv-execute-button"
          >
            取込実行
          </PoButton>
        </>
      ) : postCSVStatus.status === "error" ? (
        <PoButton
          variant="secondary"
          data-testid="post-csv-error-back-button"
          onClick={() => {
            setPostCSVStatus({
              status: "open",
              message: "",
              result: [],
              header_location: "None",
            });
            setPostCSVProductID(null);
          }}
        >
          戻る
        </PoButton>
      ) : postCSVStatus.status === "result" ? (
        <>
          {postCSVStatus.header_location != "None" ? (
            <PoButton
              variant="secondary"
              sx={{ mr: "auto" }}
              onClick={downloadErrorList}
              data-testid="post-csv-error-download"
            >
              エラー内容ダウンロード
            </PoButton>
          ) : null}
          <PoButton
            data-testid="post-csv-result-close-button"
            onClick={() => {
              rowCountSnapshotRef.current = 0;
              swCloseModal();
              setPostCSVProductID(null);
            }}
          >
            閉じる
          </PoButton>
        </>
      ) : null}
    </>
  );

  return (
    <PoModalShell
      open={onModal}
      title={postCSVTitle}
      onClose={swCloseModal}
      frameTestId="post-csv-modal"
      testidPrefix="import-flow"
      widthPx={dialogWidth}
      bodySx={{ pt: "var(--sp-4, 16px)", pb: "var(--sp-4, 16px)", gap: "var(--sp-3, 12px)" }}
      footer={footerNode}
    >
          {postCSVStatus.status === "open" && previewFile === null ? (
            <>
              {/* .pp-sec: display flex col gap sp-2 (mock m_import_preprocess_pc) */}
              <Box
                data-testid="import-flow-section"
                sx={{ display: "flex", flexDirection: "column", gap: "var(--sp-2, 8px)" }}
              >
                {/* .pp-sec-title: fs-12/bold/ai/inline-flex/center/gap sp-2 */}
                <Box
                  data-testid="import-flow-section-title"
                  sx={{
                    fontSize: "var(--fs-12, 12px)",
                    fontWeight: "var(--fw-bold, 700)",
                    color: "var(--color-ai, #1e3a5f)",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "var(--sp-2, 8px)",
                  }}
                >
                  使用するCSV項目
                </Box>
                {/* 凡例: 必須/任意の色分け説明 */}
                <Box sx={{ display: "flex", gap: "var(--sp-3, 12px)", fontSize: "var(--fs-11, 11px)", color: "var(--color-sumi-muted)" }}>
                  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "var(--sp-1, 4px)" }}>
                    <Box component="span" sx={{ width: 10, height: 10, bgcolor: "var(--color-ai)", borderRadius: 1, display: "inline-block" }} />
                    必須
                  </Box>
                  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "var(--sp-1, 4px)" }}>
                    <Box component="span" sx={{ width: 10, height: 10, bgcolor: "var(--color-white)", border: "1px solid var(--color-border)", borderRadius: 1, display: "inline-block" }} />
                    任意
                  </Box>
                </Box>
                {/* .pp-col-chips: flex/wrap/gap sp-1 */}
                <Box data-testid="import-flow-column-chips" sx={{ display: "flex", flexWrap: "wrap", gap: "var(--sp-1, 4px)" }}>
                  <>
                    {foldCSVColumns(postCSVColumn).map((chip, i) => (
                      <Box
                        key={"chip-" + i}
                        component="span"
                        data-testid="import-flow-column-chip"
                        sx={{
                          fontSize: "var(--fs-11, 11px)",
                          color: chip.required ? "var(--color-white)" : "var(--color-sumi-muted)",
                          bgcolor: chip.required ? "var(--color-ai)" : "var(--color-white)",
                          border: chip.required ? "none" : "1px solid var(--color-border)",
                          borderRadius: "var(--radius-sm, 6px)",
                          px: "var(--sp-2, 8px)",
                          py: "var(--px-3, 3px)",
                        }}
                      >
                        {chip.label}{!chip.required ? "（任意）" : ""}
                      </Box>
                    ))}
                    {(postCSVExtraChips ?? []).map((chip, i) => (
                      <Box
                        key={"extra-chip-" + i}
                        component="span"
                        data-testid="import-flow-column-chip"
                        sx={{
                          fontSize: "var(--fs-11, 11px)",
                          color: "var(--color-sumi, #374151)",
                          bgcolor: "var(--color-washi, #fafaf8)",
                          border: "1px solid var(--color-border)",
                          borderRadius: "var(--radius-sm, 6px)",
                          px: "var(--sp-2, 8px)",
                          py: "var(--px-3, 3px)",
                        }}
                      >
                        {chip}
                      </Box>
                    ))}
                  </>
                </Box>
              </Box>
              <Box
                data-testid="import-flow-sample-section"
                sx={{
                  border: "1px solid var(--color-border)",
                  borderRadius: "var(--radius-sm, 6px)",
                  p: "var(--sp-3)",
                }}
              >
                {/* .pp-sec-title: サンプルDLセクション */}
                <Box
                  data-testid="import-flow-section-title"
                  sx={{
                    fontSize: "var(--fs-12, 12px)",
                    fontWeight: "var(--fw-bold, 700)",
                    color: "var(--color-ai, #1e3a5f)",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "var(--sp-2, 8px)",
                  }}
                >
                  サンプルCSVのダウンロード
                </Box>
                {/* .sx11: サンプル行（Select/Autocomplete＋サンプルDLボタン）横並び */}
                <Box
                  data-testid="import-flow-sample-row"
                  sx={{ display: "flex", gap: "var(--sp-2, 8px)", alignItems: "center" }}
                >
                  {postCSVPath == "addContract" || postCSVPath == "entryContract" ? (
                    /* .sx3: Select側ラッパ flex:1 */
                    <Box sx={{ flex: 1 }}>
                      <PoField
                        key={"csvContractProduct"}
                        fieldKey="product"
                        testId="import-flow-field-product"
                      >
                        <PoLabel>商材</PoLabel>
                        <Autocomplete
                          sx={poAutocompleteSx}
                          clearIcon={null}
                          options={autocompleteProductList}
                          value={postCSVProductName}
                          onChange={(event: any, newValue: string | null) => {
                            if (newValue) {
                              setPostCSVProductName(newValue);
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              value={postCSVProductName}
                              {...params}
                            />
                          )}
                        />
                      </PoField>
                    </Box>
                  ) : postCSVPath == "tossUp" || postCSVPath == "tossUpAdmin" ? (
                    /* .sx3: Select側ラッパ flex:1 */
                    <Box sx={{ flex: 1 }}>
                      <PoField
                        key={"csvContractCategory"}
                        fieldKey="category"
                        testId="import-flow-field-category"
                      >
                        <PoLabel>カテゴリ</PoLabel>
                        <PoSelect
                          labelId="serch-input-label"
                          id="serch-input"
                          value={postCSVCategoryID}
                          onChange={(e) => {
                            setPostCSVCategoryID(e.target.value as number);
                          }}
                        >
                          {categoryList.map((category, i) => {
                            return (
                              <MenuItem
                                key={"category" + i}
                                value={category.id}
                                sx={{ textAlign: "right" }}
                              >
                                {category.category_name}
                              </MenuItem>
                            );
                          })}
                        </PoSelect>
                      </PoField>
                    </Box>
                  ) : null}
                  {/* .sx12: サンプルDLボタン（whiteSpace nowrap） */}
                  <PoButton
                    variant="secondary"
                    disabled={
                      (postCSVPath == "addContract" ||
                        postCSVPath == "entryContract") &&
                      postCSVProductID == null
                        ? true
                        : (postCSVPath == "tossUp" ||
                            postCSVPath == "tossUpAdmin") &&
                          postCSVCategoryID == null
                        ? true
                        : false
                    }
                    onClick={downloadSampleCSV}
                    data-testid="post-csv-sample-download"
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    サンプルCSV
                    <FileDownloadIcon
                      sx={{ marginTop: "-2.5px", marginLeft: "6px" }}
                      fontSize="medium"
                    />
                  </PoButton>
                </Box>
                {/* .po-helper: サンプルCSVの注記 */}
                {sampleNote ? (
                  <PoHelper>{sampleNote}</PoHelper>
                ) : null}
              </Box>
            </>
          ) : postCSVStatus.status === "open" && previewFile !== null ? (
            <Box sx={{ mt: 2, mb: 2 }}>
              {/* 選択ファイル */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: "grey.50",
                  mb: 2,
                }}
              >
                <AttachFileIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {previewFile.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                  {previewRowCount}行（ヘッダー除く）
                </Typography>
              </Box>

              {/* ヘッダー確認 / 0行エラー */}
              {previewRowCount === 0 ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  CSVにデータ行がありません。取込を実行できません。
                </Alert>
              ) : previewHeaderOk ? (
                <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
                  以下CSVの取込を行います
                </Alert>
              ) : (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  CSVのヘッダーが期待する形式と一致しません。取込を実行できません。
                </Alert>
              )}

              {/* サンプル行プレビュー */}
              {previewSample.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "primary.main",
                    display: "block", mb: 0.5 }}>
                    先頭サンプル（{previewSample.length}行）
                  </Typography>
                  <TableContainer component={Paper} variant="outlined"
                    sx={{ maxHeight: "30vh", borderRadius: 1 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {previewHeaders.map((h, i) => (
                            <TableCell key={"ph-" + i} sx={{ fontSize: 11, whiteSpace: "nowrap", bgcolor: "var(--color-ai)", color: "var(--color-white)" }}>
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {previewSample.map((row, ri) => (
                          <TableRow key={"pr-" + ri}>
                            {previewHeaders.map((_, ci) => (
                              <TableCell key={"pc-" + ri + "-" + ci} sx={{ fontSize: 12 }}>
                                {row[ci] ?? ""}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          ) : (postCSVStatus.status === "posting" || postCSVStatus.status === "loading") ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 4, gap: 2 }}>
              <span className="po-spinner" />
              <Box sx={{ textAlign: "center" }}>{postCSVStatus.message}</Box>
            </Box>
          ) : postCSVStatus.status === "error" ? (
            <Alert severity="error">
              <Box>CSVの取込に失敗しました。</Box>
              {!isNonHeaderCsvError(postCSVStatus.message) && (
                <Box sx={{ marginTop: "15px" }}>
                  ・質問名が異なる可能性があります。
                </Box>
              )}
              <Box sx={{ textAlign: "center", whiteSpace: "pre-line" }}>{postCSVStatus.message}</Box>
            </Alert>
          ) : postCSVStatus.status === "result" ? (
            <>
              {(() => {
                const errRows = postCSVStatus.result.filter((r) => r.message);
                const errCnt = (typeof error_count === 'number') ? error_count : errRows.length;
                const totalCnt = (typeof total_count === 'number') ? total_count : rowCountSnapshotRef.current;
                const successCnt = (typeof success_count === 'number') ? success_count : Math.max(0, totalCnt - errCnt);
                return (
                  <>
                    {resultBannerReflectsError && postCSVStatus.header_location !== "None" ? (
                      <div data-testid="import-flow-result-banner" className="info-alert -warning">
                        <span className="ia-ic">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <circle cx="12" cy="12" r="9"/>
                            <polyline points="16 9 10.5 15 8 12.5"/>
                          </svg>
                        </span>
                        <span>CSV取込完了。エラー内容をダウンロードして確認してください。</span>
                      </div>
                    ) : (
                      <Box
                        data-testid="import-flow-result-banner"
                        sx={{ display: "flex", alignItems: "center", gap: "var(--sp-2)", fontSize: 12, color: "var(--st-06-paid, #22664a)", bgcolor: "var(--st-06-paid-bg, #e6f4ee)", borderRadius: "var(--radius-sm, 6px)", p: "var(--sp-2) var(--sp-3)", mb: 1.5 }}
                      >
                        <CheckCircleIcon sx={{ fontSize: 15 }} />
                        CSV取込完了
                      </Box>
                    )}
                    {successCnt === 0 && totalCnt > 0 && (
                      <Alert severity="error" sx={{ mb: 1.5 }} data-testid="import-flow-zero-success-banner">
                        登録された行が1件もありません。CSVの内容を確認してください。
                      </Alert>
                    )}
                    {/* pp-sum: 1行帯（catalog components.css v1.10 #29） */}
                    <div
                      data-testid="import-flow-result-counts"
                      className="pp-sum"
                    >
                      <span
                        data-testid="import-flow-result-counts-ok"
                        className="pp-sum-item ok"
                      >
                        <span className="pp-sum-lbl">成功</span>
                        <span className="pp-sum-num">{successCnt}</span>
                      </span>
                      <span
                        data-testid="import-flow-result-counts-ng"
                        className="pp-sum-item ng"
                      >
                        <span className="pp-sum-lbl">失敗</span>
                        <span className="pp-sum-num">{errCnt}</span>
                      </span>
                    </div>
                  </>
                );
              })()}
              {postCSVStatus.result.length > 0 ? (
                <Box sx={{ marginTop: "15px", display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0 }}>
                  <Box
                    data-testid="import-flow-result-alert"
                    sx={{
                      fontSize: 12,
                      color: "var(--st-10-returned, #a02e27)",
                      bgcolor: "var(--st-10-returned-bg, #fbeae8)",
                      borderRadius: "var(--radius-sm, 6px)",
                      p: "var(--sp-2) var(--sp-3)",
                      mb: 1,
                    }}
                  >
                    ※以下データ登録時にエラーが発生しました。
                  </Box>
                  {/* .ce-table: width 100%/collapse */}
                  <TableContainer
                    component={Paper}
                    data-testid="import-flow-result-table"
                    sx={{
                      margin: "1px 1px",
                      width: "calc(100% - 4px)",
                      flex: "1 1 auto",
                      minHeight: 0,
                      border: "1px solid rgb(230, 230, 230)",
                    }}
                  >
                    <Table
                      stickyHeader
                      size="small"
                      aria-label="collapsible table"
                      sx={{ minWidth: "max-content" }}
                    >
                      <TableHead>
                        <TableRow key="errorCSVTableRow">
                          {/* .ce-table th: fs-11/bold/sumi-light/left/sp-2 pad/border/washi */}
                          <TableCell key={"errorCSVTableMessage"} align="left" sx={{ fontSize: "var(--fs-11, 11px)", fontWeight: "var(--fw-bold, 700)", color: "var(--color-white)", bgcolor: "var(--color-ai)", padding: "var(--sp-2, 8px) var(--sp-2, 8px)", borderBottom: "1px solid var(--color-border, #e5e5e0)", whiteSpace: "nowrap" }}>
                            エラーメッセージ
                          </TableCell>
                          {postCSVColumnResult.map((column, i) => {
                            return (
                              <TableCell
                                key={"errorCSVTableColumn" + i}
                                align="left"
                                sx={{ fontSize: "var(--fs-11, 11px)", fontWeight: "var(--fw-bold, 700)", color: "var(--color-white)", bgcolor: "var(--color-ai)", padding: "var(--sp-2, 8px) var(--sp-2, 8px)", borderBottom: "1px solid var(--color-border, #e5e5e0)", whiteSpace: "nowrap" }}
                              >
                                {column}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {postCSVStatus.result.map((result, i) => (
                          <Fragment key={"errorCSVTableFragment" + i}>
                            <TableRow>
                              {/* .ce-table td: fs-13/sumi/sp-2 pad/border-light */}
                              <TableCell
                                key={"errorCSVTableCell" + i}
                                align="left"
                                sx={{ fontSize: "var(--fs-13, 13px)", color: "var(--color-sumi, #374151)", padding: "var(--sp-2, 8px) var(--sp-2, 8px)", borderBottom: "1px solid var(--color-border-light, #efefea)", whiteSpace: "nowrap" }}
                              >
                                {result.message}
                              </TableCell>
                              {postCSVColumnResult.map((column, column_i) => {
                                return (
                                  <TableCell
                                    key={
                                      "errorCSVTableColumn" +
                                      i +
                                      "Data" +
                                      column_i
                                    }
                                    align="left"
                                    sx={{ fontSize: "var(--fs-13, 13px)", color: "var(--color-sumi, #374151)", padding: "var(--sp-2, 8px) var(--sp-2, 8px)", borderBottom: "1px solid var(--color-border-light, #efefea)", whiteSpace: "nowrap" }}
                                  >
                                    {result.postData[column]}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          </Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ) : null}
            </>
          ) : null}
    </PoModalShell>
  );
}
